import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ApartmentOutlined,
  DashboardOutlined,
  DeleteOutlined,
  EditOutlined,
  OrderedListOutlined,
  PlusOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { Button, Popconfirm, Space, Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import dayjs from 'dayjs';

import InitiativeModal from '@totem/components/portfolio/buildings/details/optimize/InitiativeModal';
import InitiativesDrawer from '@totem/components/portfolio/buildings/details/optimize/InitiativesDrawer';
import OptimizeContext from '@totem/components/portfolio/buildings/details/optimize/OptimizeContext';
import ProgramModal from '@totem/components/portfolio/buildings/details/optimize/ProgramModal';
import ProgramPhasesDrawer from '@totem/components/portfolio/buildings/details/optimize/ProgramPhasesDrawer';
import {
  Initiative,
  Program,
  PROGRAM_TYPES,
} from '@totem/components/portfolio/buildings/details/optimize/types';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { BUILDINGS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const statusColors: Record<string, string> = {
  active: 'green',
  complete: 'blue',
  archived: 'default',
};

const typeLabel = (val: string) =>
  PROGRAM_TYPES.find((typ) => typ.value === val)?.label ?? val;

const formatMonth = (dateStr: string) =>
  dateStr ? dayjs(dateStr).format('MMM YYYY') : '—';

const OptimizePanel = () => {
  const { getApiToken } = useContext(UserProfileContext);
  const { programs, loading, buildingId, requestRefresh } =
    useContext(OptimizeContext);

  const [modalOpen, setModalOpen] = useState(false);
  const [editProgram, setEditProgram] = useState<Program | null>(null);
  const [phasesProgram, setPhasesProgram] = useState<Program | null>(null);
  const [phasesOpen, setPhasesOpen] = useState(false);
  const [initiativesProgram, setInitiativesProgram] = useState<Program | null>(
    null,
  );
  const [initiativesOpen, setInitiativesOpen] = useState(false);
  const [recalculating, setRecalculating] = useState<string | null>(null);
  const [initiativeModalOpen, setInitiativeModalOpen] = useState(false);
  const [initiativeEditRecord, setInitiativeEditRecord] =
    useState<Initiative | null>(null);
  const [initiativeRefreshTrigger, setInitiativeRefreshTrigger] = useState(0);

  const handleDelete = (prog: Program) => {
    fetch(`${BUILDINGS_ENDPOINT}/${buildingId}/optimize/programs/${prog.id}`, {
      method: 'DELETE',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
    }).then((res) => {
      CheckResponseShowError(res);

      requestRefresh();
    });
  };

  const handleRecalculate = (prog: Program) => {
    setRecalculating(prog.id);

    fetch(
      `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/programs/${prog.id}/recalculate`,
      {
        method: 'POST',
        headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
      },
    )
      .then((res) => CheckResponseShowError(res))
      .finally(() => setRecalculating(null));
  };

  const columns: ColumnProps<Program>[] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (rowA, rowB) => sortStringAscending(rowA.name, rowB.name),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (_, record) => (
        <Tag color={statusColors[record.status] ?? 'default'}>
          {record.status}
        </Tag>
      ),
    },
    {
      title: 'Types',
      key: 'types',
      render: (_, record) =>
        (record.types ?? []).map((val) => (
          <Tag key={val}>{typeLabel(val)}</Tag>
        )),
    },
    {
      title: 'Baseline Period',
      key: 'baseline',
      render: (_, record) =>
        `${formatMonth(record.baselinePeriodStart)} – ${formatMonth(record.baselinePeriodEnd)}`,
    },
    {
      title: '',
      key: 'actions',
      align: 'right' as const,
      render: (_, record) => (
        <Space>
          <Link
            to={`/dashboard/optimize/buildings/${buildingId}/programs/${record.id}/dashboard`}
          >
            <Button size="small" icon={<DashboardOutlined />}>
              Dashboard
            </Button>
          </Link>
          <Button
            size="small"
            icon={<ApartmentOutlined />}
            onClick={() => {
              setInitiativesProgram(record);

              setInitiativesOpen(true);
            }}
          >
            Initiatives
          </Button>
          <Button
            size="small"
            icon={<OrderedListOutlined />}
            onClick={() => {
              setPhasesProgram(record);

              setPhasesOpen(true);
            }}
          >
            Phases
          </Button>
          <Button
            size="small"
            icon={<RedoOutlined />}
            loading={recalculating === record.id}
            onClick={() => handleRecalculate(record)}
          >
            Recalculate
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditProgram(record);

              setModalOpen(true);
            }}
          />
          <Popconfirm
            title="Delete this program?"
            description="This cannot be undone."
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            okType="danger"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <IBCard
        isCovered
        title="Optimize Programs"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditProgram(null);

              setModalOpen(true);
            }}
          >
            Add Program
          </Button>
        }
      >
        <IBTable
          columns={columns}
          dataSource={isNotNull(programs) ? programs : []}
          loading={loading}
          rowKey={(record) => record.id}
          pagination={{ showSizeChanger: true }}
        />
      </IBCard>

      <ProgramModal
        open={modalOpen}
        program={editProgram}
        onClose={() => {
          setModalOpen(false);

          setEditProgram(null);
        }}
      />

      {isNotNull(phasesProgram) && (
        <ProgramPhasesDrawer
          open={phasesOpen}
          buildingId={buildingId}
          program={phasesProgram}
          onClose={() => {
            setPhasesOpen(false);

            setPhasesProgram(null);
          }}
        />
      )}

      {isNotNull(initiativesProgram) && (
        <InitiativesDrawer
          open={initiativesOpen}
          buildingId={buildingId}
          program={initiativesProgram}
          refreshTrigger={initiativeRefreshTrigger}
          onClose={() => {
            setInitiativesOpen(false);

            setInitiativesProgram(null);
          }}
          onRequestAdd={() => {
            setInitiativeEditRecord(null);

            setInitiativeModalOpen(true);
          }}
          onRequestEdit={(initiative) => {
            setInitiativeEditRecord(initiative);

            setInitiativeModalOpen(true);
          }}
        />
      )}

      <InitiativeModal
        open={initiativeModalOpen}
        buildingId={buildingId}
        programId={initiativesProgram?.id ?? ''}
        initiative={initiativeEditRecord}
        onClose={() => {
          setInitiativeModalOpen(false);

          setInitiativeEditRecord(null);
        }}
        onSaved={() => setInitiativeRefreshTrigger((prev) => prev + 1)}
      />
    </>
  );
};

export default OptimizePanel;
